import { isMonitoringEnabled, getSentry, getErrorRules } from './monitoring.js';
/**
 * Custom error classes for better Sentry grouping
 */
class Http5xxError extends Error {
    status;
    originalError;
    constructor(message, status, originalError) {
        super(message);
        this.status = status;
        this.originalError = originalError;
        this.name = `HTTP ${status} Error`;
    }
}
class Http4xxError extends Error {
    status;
    originalError;
    constructor(message, status, originalError) {
        super(message);
        this.status = status;
        this.originalError = originalError;
        this.name = `HTTP ${status} Error`;
    }
}
class NetworkError extends Error {
    originalError;
    constructor(message, originalError) {
        super(message);
        this.originalError = originalError;
        this.name = 'Network Error';
    }
}
class TimeoutError extends Error {
    originalError;
    constructor(message, originalError) {
        super(message);
        this.originalError = originalError;
        this.name = 'Timeout Error';
    }
}
/**
 * Extract meaningful endpoint name from URL for monitoring
 */
export function sanitizeEndpoint(url) {
    try {
        const urlObj = new URL(url);
        const path = urlObj.pathname;
        // Remove IDs and common dynamic segments
        return path
            .replace(/\/\d+/g, '/:id')
            .replace(/\/[a-f0-9-]{36}/gi, '/:uuid')
            .replace(/\/[a-f0-9]{24}/gi, '/:objectid')
            .replace(/\?.+$/, ''); // Remove query params
    }
    catch {
        // If URL parsing fails, return sanitized version
        return url.replace(/[?#].*$/, '').substring(0, 100);
    }
}
/**
 * Classify error based on universal error rules and response metadata
 */
function classifyError(error, responseMetadata) {
    const rules = getErrorRules();
    // Extract status from axios errors or responseMetadata
    const errorStatus = error?.response?.status || responseMetadata?.status;
    // Network errors (fetch failures, connection issues)
    // Check for axios network errors (no response) or fetch errors
    if ((error?.code === 'ERR_NETWORK' ||
        error?.code === 'ECONNREFUSED' ||
        (error.name === 'TypeError' && error.message.includes('fetch'))) &&
        !errorStatus // Only if there's no HTTP status (true network error)
    ) {
        return {
            level: 'error',
            shouldCapture: rules.captureNetworkErrors,
        };
    }
    // Timeout/abort errors
    if (error?.code === 'ECONNABORTED' ||
        error.name === 'AbortError' ||
        error.message.includes('timeout')) {
        return {
            level: 'warning',
            shouldCapture: rules.captureTimeouts,
        };
    }
    // HTTP error responses (check both axios error and responseMetadata)
    if (errorStatus) {
        // 5xx errors
        if (errorStatus >= 500) {
            return {
                level: 'error',
                shouldCapture: rules.captureOn5xx,
                status: errorStatus,
            };
        }
        // 4xx errors
        if (errorStatus >= 400) {
            return {
                level: 'warning',
                status: errorStatus,
                shouldCapture: rules.captureOn4xx,
            };
        }
    }
    // Default: don't capture (let specific rules determine)
    return { level: 'error', shouldCapture: false };
}
/**
 * Generic monitored request wrapper that instruments any async function
 *
 * This wrapper automatically:
 * - Captures errors based on universal rules defined in initMonitoring()
 * - Flags responses exceeding the AWS Lambda response limit (if size is provided)
 * - Adds breadcrumbs for debugging
 * - Reports to Sentry with proper context
 *
 * Endpoint slowness is not captured here; it is monitored via Sentry tracing
 * (http.client span durations).
 *
 * @param requestFn - The async function to execute and monitor
 * @param metadata - Request metadata (endpoint, method, tags)
 * @param responseExtractor - Optional function to extract response metadata
 * @returns Promise resolving to the result of requestFn
 *
 * @example
 * ```typescript
 * // Frontend with API client
 * const result = await monitoredRequest(
 *   () => API.get(apiName, '/kits', {}),
 *   { endpoint: '/kits', method: 'GET', tags: { feature: 'kits' } }
 * );
 *
 * // With response metadata
 * const result = await monitoredRequest(
 *   () => API.post(apiName, '/kits', { body: data }),
 *   { endpoint: '/kits', method: 'POST' },
 *   (result) => ({ status: result.status, size: result.size, success: true })
 * );
 * ```
 */
export async function monitoredRequest(requestFn, metadata, responseExtractor) {
    if (!isMonitoringEnabled()) {
        // Monitoring disabled, just execute the request
        return requestFn();
    }
    const rules = getErrorRules();
    const sentry = getSentry();
    const startTime = Date.now();
    const { endpoint, method, tags, context } = metadata;
    // Add breadcrumb for request start
    if (sentry) {
        sentry.addBreadcrumb({
            category: 'http',
            message: `${method} ${endpoint}`,
            level: 'info',
            data: {
                endpoint,
                method,
            },
        });
    }
    let result;
    let responseMeta;
    let error;
    try {
        result = await requestFn();
        // Extract response metadata if provided
        if (responseExtractor && result !== undefined) {
            responseMeta = responseExtractor(result);
            // Check response size if provided
            if (responseMeta.size) {
                // Critical: AWS Lambda synchronous response limit (6MB)
                const LAMBDA_RESPONSE_LIMIT = 6_000_000;
                if (responseMeta.size > LAMBDA_RESPONSE_LIMIT && sentry) {
                    sentry.captureMessage('Response exceeds AWS Lambda limit', {
                        level: 'error',
                        tags: {
                            endpoint,
                            method,
                            response_size_issue: 'lambda_limit',
                            ...tags,
                        },
                        extra: {
                            size: responseMeta.size,
                            sizeInMB: (responseMeta.size / 1_000_000).toFixed(2),
                            limit: LAMBDA_RESPONSE_LIMIT,
                            limitInMB: (LAMBDA_RESPONSE_LIMIT / 1_000_000).toFixed(2),
                            ...context,
                        },
                    });
                }
            }
        }
        return result;
    }
    catch (err) {
        // Handle special cases that should be re-thrown without modification
        // TanStack Router's redirect() throws a Redirect object that must pass through
        if (err && typeof err === 'object' && 'to' in err && 'throw' in err) {
            throw err; // Re-throw Redirect objects from TanStack Router
        }
        // Handle 3xx redirect responses - re-throw to let the framework handle them
        // In TanStack Start SSR context, redirect() throws Response objects with 307 status
        if (err instanceof Response && err.status >= 300 && err.status < 400) {
            throw err;
        }
        // Handle Response objects (from fetch API error patterns)
        if (err instanceof Response) {
            error = new Error(`HTTP ${err.status} ${err.statusText || 'Error'}`);
            error.status = err.status;
            error.response = err;
        }
        else {
            error = err instanceof Error ? err : new Error(String(err));
        }
        const { level, shouldCapture, status } = classifyError(error, responseMeta);
        if (shouldCapture && sentry) {
            // Determine error type and create custom error with descriptive name
            let errorType = 'unknown';
            let errorToCapture = error;
            const errorStatus = status || error?.response?.status;
            if (errorStatus) {
                if (errorStatus >= 500) {
                    errorType = 'http_5xx';
                    errorToCapture = new Http5xxError(`${method} ${endpoint} failed with status ${errorStatus}`, errorStatus, error);
                }
                else if (errorStatus >= 400) {
                    errorType = 'http_4xx';
                    errorToCapture = new Http4xxError(`${method} ${endpoint} failed with status ${errorStatus}`, errorStatus, error);
                }
            }
            else if (error?.code === 'ECONNABORTED' ||
                error.message.includes('timeout')) {
                errorType = 'timeout';
                errorToCapture = new TimeoutError(`${method} ${endpoint} timed out`, error);
            }
            else if (error?.code === 'ERR_NETWORK') {
                errorType = 'network';
                errorToCapture = new NetworkError(`${method} ${endpoint} - Network request failed`, error);
            }
            sentry.captureException(errorToCapture, {
                level,
                tags: {
                    main_error: true, // Errors captured by universal error rules
                    endpoint,
                    method,
                    error_type: errorType,
                    ...(errorStatus && { status: errorStatus }),
                    ...tags,
                },
                extra: {
                    endpoint,
                    originalError: error.message,
                    originalErrorName: error.name,
                    errorCode: error?.code,
                    ...(errorStatus && { httpStatus: errorStatus }),
                    ...context,
                },
            });
        }
        throw error;
    }
    finally {
        const duration = Date.now() - startTime;
        // Log performance metrics if enabled
        if (rules.logPerformance) {
            console.log('[Request Metrics]', {
                endpoint,
                method,
                duration,
                status: responseMeta?.status,
                responseSize: responseMeta?.size,
                success: !error && (responseMeta?.success ?? true),
                error,
            });
        }
        // Add breadcrumb for request completion
        if (sentry) {
            sentry.addBreadcrumb({
                category: 'http',
                message: `${method} ${endpoint} - ${responseMeta?.status || (error ? 'failed' : 'success')}`,
                level: error ? 'error' : 'info',
                data: {
                    endpoint,
                    method,
                    status: responseMeta?.status,
                    duration,
                    success: !error && (responseMeta?.success ?? true),
                },
            });
        }
    }
}
/**
 * Create a monitored request function with preset metadata
 *
 * @example
 * ```typescript
 * const monitoredKitsApi = createMonitoredRequest({
 *   tags: { service: 'api', feature: 'kits' }
 * });
 *
 * const result = await monitoredKitsApi(
 *   () => API.get(apiName, '/kits', {}),
 *   { endpoint: '/kits', method: 'GET' }
 * );
 * ```
 */
export function createMonitoredRequest(defaultMetadata) {
    return (requestFn, metadata, responseExtractor) => monitoredRequest(requestFn, { ...defaultMetadata, ...metadata }, responseExtractor);
}
//# sourceMappingURL=monitored-request.js.map